import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { API_URL } from "../config/api";
import { humanizeGemLabel } from "../constants/gems";
import { formatKeyCostLines } from "../constants/keys";
import { formatPotionCostLines } from "../constants/potions";
import {
  buildAutoCommonGemAllocations,
  computeMaxAffordableQuantity,
  getOwnedCommonGems,
} from "../utils/potionAffordability";
import "./PotionExchangeModal.css";

const EXCHANGE_CONFIG = {
  potion: {
    categoryLabel: "potions",
    apiPath: "potions/obtain",
    idField: "potionId",
    defaultError: "Impossible d'obtenir cette potion.",
  },
  key: {
    categoryLabel: "parchemins",
    apiPath: "keys/obtain",
    idField: "keyId",
    defaultError: "Impossible d'obtenir ce parchemin.",
  },
};

function getInitialPaymentMode(item, gemCounts) {
  const maxSpecific = computeMaxAffordableQuantity(item, gemCounts, "specific");
  if (maxSpecific >= 1) return "specific";
  return "common";
}

function formatGemLabel(gemId) {
  return humanizeGemLabel(`${gemId}.png`).toLocaleLowerCase("fr-FR");
}

function formatGemPluralLabel(gemId) {
  const label = formatGemLabel(gemId);
  if (label === "rubis") return "rubis";
  if (label.endsWith("s")) return label;
  return `${label}s`;
}

function formatSpecificGemRadioLabel(gemId) {
  return `Utiliser les ${formatGemPluralLabel(gemId)}`;
}

function formatMissingSpecificGemMessage(gemId) {
  const label = formatGemLabel(gemId);
  if (/^[aeiouhâàäéèêëïîôùûüy]/i.test(label)) {
    return `Vous n'avez pas d'${label}`;
  }
  return `Vous n'avez pas de ${label}`;
}

function QuantityStepper({
  value,
  min,
  max,
  disabled,
  onChange,
  ariaLabel,
}) {
  const canDecrement = !disabled && value > min;
  const canIncrement = !disabled && value < max;

  return (
    <div className="potion-exchange-modal__stepper" aria-label={ariaLabel}>
      <button
        type="button"
        className="potion-exchange-modal__stepper-btn"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={!canDecrement}
        aria-label="Diminuer"
      >
        <ChevronDown size={16} strokeWidth={2.5} />
      </button>
      <input
        type="text"
        readOnly
        tabIndex={-1}
        className="potion-exchange-modal__stepper-value"
        value={value}
        aria-live="polite"
        aria-label={`Quantité : ${value}`}
      />
      <button
        type="button"
        className="potion-exchange-modal__stepper-btn"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={!canIncrement}
        aria-label="Augmenter"
      >
        <ChevronUp size={16} strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default function ExchangeModal({
  open,
  item,
  itemKind,
  gems,
  gemCounts,
  onClose,
  onSuccess,
}) {
  const config = EXCHANGE_CONFIG[itemKind];
  const [paymentMode, setPaymentMode] = useState("specific");
  const [quantity, setQuantity] = useState(1);
  const [allocations, setAllocations] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [gemListScrollFades, setGemListScrollFades] = useState({
    top: false,
    bottom: false,
  });
  const gemListRef = useRef(null);
  const exchangeSessionRef = useRef(null);
  const skipAllocationResetRef = useRef(false);

  const costLines = useMemo(() => {
    if (!item) return null;
    if (itemKind === "potion") return formatPotionCostLines(item);
    return formatKeyCostLines(item);
  }, [item, itemKind]);

  const allowsCommonPayment = (item?.cost?.commonGemCount ?? 0) > 0;

  const ownedCommonGems = useMemo(() => getOwnedCommonGems(gems), [gems]);

  const maxQuantity = useMemo(
    () => computeMaxAffordableQuantity(item, gemCounts, paymentMode),
    [item, gemCounts, paymentMode]
  );

  const requiredCommonTotal = useMemo(() => {
    const perUnit = item?.cost?.commonGemCount ?? 0;
    return perUnit * quantity;
  }, [item, quantity]);

  const selectedCommonTotal = useMemo(
    () =>
      Object.values(allocations).reduce(
        (sum, value) => sum + (Number(value) || 0),
        0
      ),
    [allocations]
  );

  const specificRadioLabel = useMemo(() => {
    if (!item?.cost?.specificGemId) return "Utiliser la pierre affinité";
    return formatSpecificGemRadioLabel(item.cost.specificGemId);
  }, [item]);

  const specificGemOwned = useMemo(() => {
    if (!item?.cost?.specificGemId) return 0;
    return Number(gemCounts[item.cost.specificGemId]) || 0;
  }, [item, gemCounts]);

  const canUseSpecificPayment = useMemo(
    () => computeMaxAffordableQuantity(item, gemCounts, "specific") >= 1,
    [item, gemCounts]
  );

  const specificRadioDetail = useMemo(() => {
    if (!item?.cost?.specificGemId) return costLines?.specificPart ?? "";
    if (specificGemOwned <= 0) {
      return formatMissingSpecificGemMessage(item.cost.specificGemId);
    }
    return `${costLines.specificPart}${quantity > 1 ? ` × ${quantity}` : ""}`;
  }, [item, costLines, specificGemOwned, quantity]);

  const updateGemListScrollFades = useCallback(() => {
    const el = gemListRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;

    setGemListScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  const canValidate =
    maxQuantity >= quantity &&
    quantity >= 1 &&
    (paymentMode === "specific"
      ? true
      : selectedCommonTotal === requiredCommonTotal);

  useEffect(() => {
    if (!open || !item) {
      exchangeSessionRef.current = null;
      return;
    }

    const sessionKey = `${itemKind}:${item.id}`;
    if (exchangeSessionRef.current === sessionKey) return;

    exchangeSessionRef.current = sessionKey;
    const initialPayment =
      allowsCommonPayment && computeMaxAffordableQuantity(item, gemCounts, "specific") < 1
        ? "common"
        : getInitialPaymentMode(item, gemCounts);
    setPaymentMode(initialPayment);
    setQuantity(1);
    setAllocations({});
    setSubmitting(false);
    setError(null);
  }, [open, item, itemKind, gemCounts, allowsCommonPayment]);

  useEffect(() => {
    if (maxQuantity < 1) {
      setQuantity(1);
      return;
    }
    setQuantity((current) => Math.min(Math.max(1, current), maxQuantity));
  }, [maxQuantity]);

  useEffect(() => {
    if (skipAllocationResetRef.current) {
      skipAllocationResetRef.current = false;
      return;
    }
    setAllocations({});
    setError(null);
  }, [paymentMode, quantity]);

  useEffect(() => {
    if (!allowsCommonPayment && paymentMode === "common") {
      setPaymentMode("specific");
    }
  }, [allowsCommonPayment, paymentMode]);

  useEffect(() => {
    updateGemListScrollFades();
  }, [open, ownedCommonGems, paymentMode, updateGemListScrollFades]);

  useEffect(() => {
    const el = gemListRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(updateGemListScrollFades);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open, updateGemListScrollFades]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !item || !costLines || !config) return null;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handlePaymentModeChange = (nextMode) => {
    if (submitting || nextMode === paymentMode) return;
    if (nextMode === "specific" && !canUseSpecificPayment) return;
    setPaymentMode(nextMode);
  };

  const handleGemAllocationChange = (gemId, gemCount, currentValue, nextValue) => {
    const othersTotal = selectedCommonTotal - currentValue;
    const capped = Math.min(
      gemCount,
      Math.max(0, requiredCommonTotal - othersTotal),
      nextValue
    );
    setAllocations((prev) => ({ ...prev, [gemId]: capped }));
  };

  const handleAutoChooseCommon = () => {
    if (submitting || !allowsCommonPayment) return;
    const nextAllocations = buildAutoCommonGemAllocations(
      ownedCommonGems,
      requiredCommonTotal
    );
    if (paymentMode !== "common") {
      skipAllocationResetRef.current = true;
      setPaymentMode("common");
    }
    setAllocations(nextAllocations);
    setError(null);
  };

  const submitExchange = async () => {
    setSubmitting(true);
    setError(null);

    const payload = {
      [config.idField]: item.id,
      payment: paymentMode,
      quantity,
    };

    if (paymentMode === "common") {
      const selected = Object.entries(allocations).filter(
        ([, count]) => (Number(count) || 0) > 0
      );
      payload.allocations = Object.fromEntries(selected);
    }

    try {
      const res = await fetch(`${API_URL}/${config.apiPath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.message || config.defaultError);
      }

      onSuccess?.(result);
      onClose();
    } catch (err) {
      setError(err.message || "Erreur lors de l'échange.");
    } finally {
      setSubmitting(false);
    }
  };

  const obtainArticle = item.obtainArticle ?? "une";
  const obtainTarget =
    itemKind === "key"
      ? `${obtainArticle} parchemin ${item.name}`
      : `${obtainArticle} ${item.name}`;
  const titleId = `${itemKind}-exchange-modal-title`;
  const commonSelectionDisabled = paymentMode !== "common" || submitting;
  const exchangeTitle = `Fenêtre d'échange des ${config.categoryLabel}`;

  return createPortal(
    <div
      className="potion-exchange-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
    >
      <div
        className="potion-exchange-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <button
          type="button"
          className="potion-exchange-modal__close"
          onClick={handleClose}
          disabled={submitting}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3 id={titleId} className="potion-exchange-modal__title">
          {exchangeTitle}
        </h3>

        <div className="potion-exchange-modal__intro">
          <div className="potion-exchange-modal__intro-text">
            <p className="potion-exchange-modal__text">
              Pour obtenir <strong>{obtainTarget}</strong>, vous pouvez utiliser au
              choix une pierre affinité ou des pierres communes.
            </p>
          </div>

          {item.image && (
            <div
              className={`potion-exchange-modal__intro-visual${
                itemKind === "key" ? " potion-exchange-modal__intro-visual--key" : ""
              }`}
            >
              <img
                src={item.image}
                alt=""
                className={`potion-exchange-modal__item-image${
                  itemKind === "key" ? " potion-exchange-modal__item-image--key" : ""
                }`}
                decoding="sync"
                draggable={false}
              />
            </div>
          )}
        </div>

        <fieldset className="potion-exchange-modal__payment-fieldset">
          <legend className="potion-exchange-modal__payment-legend">
            Mode de paiement
          </legend>
          <div className="potion-exchange-modal__radios">
            <label
              className={`potion-exchange-modal__radio${
                paymentMode === "specific"
                  ? " potion-exchange-modal__radio--checked"
                  : ""
              }${
                !canUseSpecificPayment
                  ? " potion-exchange-modal__radio--disabled"
                  : ""
              }`}
            >
              <input
                type="radio"
                name={`${itemKind}-payment`}
                value="specific"
                checked={paymentMode === "specific"}
                onChange={() => handlePaymentModeChange("specific")}
                disabled={submitting || !canUseSpecificPayment}
              />
              <span className="potion-exchange-modal__radio-mark" aria-hidden="true" />
              <span className="potion-exchange-modal__radio-body">
                <span className="potion-exchange-modal__radio-title">
                  {specificRadioLabel}
                </span>
                <span
                  className={`potion-exchange-modal__radio-detail${
                    specificGemOwned <= 0
                      ? " potion-exchange-modal__radio-detail--missing"
                      : ""
                  }`}
                >
                  {specificRadioDetail}
                </span>
              </span>
            </label>

            <label
              className={`potion-exchange-modal__radio${
                paymentMode === "common"
                  ? " potion-exchange-modal__radio--checked"
                  : ""
              }${!allowsCommonPayment ? " potion-exchange-modal__radio--disabled" : ""}`}
            >
              <input
                type="radio"
                name={`${itemKind}-payment`}
                value="common"
                checked={paymentMode === "common"}
                onChange={() => handlePaymentModeChange("common")}
                disabled={submitting || !allowsCommonPayment}
              />
              <span className="potion-exchange-modal__radio-mark" aria-hidden="true" />
              <span className="potion-exchange-modal__radio-body">
                <span className="potion-exchange-modal__radio-title">
                  Utiliser les pierres communes
                </span>
                <span className="potion-exchange-modal__radio-detail">
                  {requiredCommonTotal > 1
                    ? `${requiredCommonTotal} pierres communes`
                    : "1 pierre commune"}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <div className="potion-exchange-modal__quantity-row">
          <span className="potion-exchange-modal__quantity-label">Quantité</span>
          <QuantityStepper
            value={quantity}
            min={1}
            max={Math.max(1, maxQuantity)}
            disabled={submitting || maxQuantity < 1}
            onChange={setQuantity}
            ariaLabel="Quantité à obtenir"
          />
          {maxQuantity > 0 && (
            <span className="potion-exchange-modal__quantity-max">
              Vous pouvez en acheter au maximum&nbsp;:{" "}
              <strong>{maxQuantity}</strong>
            </span>
          )}
        </div>

        <p
          className={`potion-exchange-modal__progress${
            paymentMode !== "common" ? " potion-exchange-modal__progress--hidden" : ""
          }`}
          aria-hidden={paymentMode !== "common"}
        >
          Sélection&nbsp;: <strong>{selectedCommonTotal}</strong> /{" "}
          {requiredCommonTotal}
        </p>

        <div
          className={[
            "potion-exchange-modal__gem-list-shell",
            gemListScrollFades.top && "potion-exchange-modal__gem-list-shell--fade-top",
            gemListScrollFades.bottom &&
              "potion-exchange-modal__gem-list-shell--fade-bottom",
            commonSelectionDisabled && "potion-exchange-modal__gem-list-shell--disabled",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-disabled={commonSelectionDisabled}
        >
          <div
            ref={gemListRef}
            className="potion-exchange-modal__gem-list"
            onScroll={updateGemListScrollFades}
          >
            {ownedCommonGems.map((gem) => {
              const value = allocations[gem.id] ?? 0;
              const maxAllocatable = Math.min(
                gem.count,
                Math.max(0, requiredCommonTotal - selectedCommonTotal + value)
              );
              return (
                <div key={gem.id} className="potion-exchange-modal__gem-row">
                  <div className="potion-exchange-modal__gem-info">
                    <img
                      src={gem.imageUrl}
                      alt=""
                      className="potion-exchange-modal__gem-thumb"
                      decoding="sync"
                      draggable={false}
                    />
                    <span className="potion-exchange-modal__gem-name">
                      {gem.label}
                    </span>
                  </div>
                  <span className="potion-exchange-modal__gem-owned">
                    Possédées&nbsp;: <strong>{gem.count}</strong>
                  </span>
                  <div className="potion-exchange-modal__gem-give">
                    <span className="potion-exchange-modal__gem-give-label">
                      À céder
                    </span>
                    <QuantityStepper
                      value={value}
                      min={0}
                      max={maxAllocatable}
                      disabled={commonSelectionDisabled}
                      onChange={(next) =>
                        handleGemAllocationChange(gem.id, gem.count, value, next)
                      }
                      ariaLabel={`Pierres ${gem.label} à céder`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="potion-exchange-modal__actions">
          <button
            type="button"
            className="potion-exchange-modal__btn potion-exchange-modal__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Annuler
          </button>
          <div className="potion-exchange-modal__actions-primary">
            <button
              type="button"
              className="potion-exchange-modal__btn potion-exchange-modal__btn--auto"
              onClick={handleAutoChooseCommon}
              disabled={commonSelectionDisabled}
            >
              Choisir pour moi
            </button>
            <button
              type="button"
              className="potion-exchange-modal__btn potion-exchange-modal__btn--confirm"
              onClick={submitExchange}
              disabled={submitting || !canValidate}
            >
              {submitting ? "Échange…" : "Valider l'échange"}
            </button>
          </div>
        </div>

        {error && <p className="potion-exchange-modal__error">{error}</p>}
      </div>
    </div>,
    getAppPortalRoot()
  );
}
